import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import SupportTicketsAPI from '../../api/supportTickets';

export const state = {
  records: [],
  currentTicket: null,
  messages: [],
  meta: {
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 20,
  },
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isCreatingMessage: false,
    isUpdating: false,
  },
};

export const getters = {
  getTickets(_state) {
    return _state.records;
  },
  getTicket(_state) {
    return _state.currentTicket;
  },
  getMessages(_state) {
    return _state.messages;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getMeta(_state) {
    return _state.meta;
  },
  getTicketById: _state => id => {
    return _state.records.find(record => record.id === Number(id));
  },
};

export const actions = {
  get: async function getTickets({ commit }, params = {}) {
    commit(types.SET_SUPPORT_TICKETS_UI_FLAG, { isFetching: true });
    try {
      const response = await SupportTicketsAPI.get(params);
      // 兼容不同格式：response.data.data 或 response.data
      const tickets = response.data?.data || response.data || [];
      const meta = response.data?.meta || {};

      commit(types.SET_SUPPORT_TICKETS, tickets);
      commit(types.SET_SUPPORT_TICKETS_META, meta);
      return { tickets, meta };
    } finally {
      commit(types.SET_SUPPORT_TICKETS_UI_FLAG, { isFetching: false });
    }
  },

  show: async function getTicket({ commit }, ticketId) {
    commit(types.SET_SUPPORT_TICKETS_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await SupportTicketsAPI.show(ticketId);
      // 后端返回格式: { data: {...ticket}, messages: [...] }
      // axios 会外层包装: { data: { data: {...ticket}, messages: [...] } }
      const ticket = response.data.data || response.data;
      const messages = response.data.messages || [];
      commit(types.SET_SUPPORT_TICKET, ticket);
      commit(types.SET_SUPPORT_TICKET_MESSAGES, messages);
      return { ...ticket, messages };
    } finally {
      commit(types.SET_SUPPORT_TICKETS_UI_FLAG, { isFetchingItem: false });
    }
  },

  create: async function createTicket({ commit }, ticketData) {
    commit(types.SET_SUPPORT_TICKETS_UI_FLAG, { isCreating: true });
    try {
      const response = await SupportTicketsAPI.create(ticketData);
      // 后端返回格式: { data: { ...ticket } }，需要访问 response.data.data
      const ticket = response.data.data || response.data;
      commit(types.ADD_SUPPORT_TICKET, ticket);
      return ticket;
    } catch (error) {
      // 重新抛出错误，让父组件能够捕获并处理
      throw error;
    } finally {
      commit(types.SET_SUPPORT_TICKETS_UI_FLAG, { isCreating: false });
    }
  },

  close: async function closeTicket({ commit }, ticketId) {
    commit(types.SET_SUPPORT_TICKETS_UI_FLAG, { isUpdating: true });
    try {
      await SupportTicketsAPI.close(ticketId);
      commit(types.UPDATE_SUPPORT_TICKET_STATUS, {
        id: ticketId,
        status: 'closed',
      });
    } finally {
      commit(types.SET_SUPPORT_TICKETS_UI_FLAG, { isUpdating: false });
    }
  },

  urge: async function urgeTicket({ commit }, ticketId) {
    commit(types.SET_SUPPORT_TICKETS_UI_FLAG, { isUpdating: true });
    try {
      await SupportTicketsAPI.urge(ticketId);
    } finally {
      commit(types.SET_SUPPORT_TICKETS_UI_FLAG, { isUpdating: false });
    }
  },

  sendMessage: async function sendMessage(
    { commit },
    { ticketId, messageData }
  ) {
    commit(types.SET_SUPPORT_TICKETS_UI_FLAG, { isCreatingMessage: true });
    try {
      const response = await SupportTicketsAPI.sendMessage(
        ticketId,
        messageData
      );
      // 后端返回格式: { data: { ...message } }
      const message = response.data.data || response.data;
      commit(types.ADD_SUPPORT_TICKET_MESSAGE, message);
      return message;
    } finally {
      commit(types.SET_SUPPORT_TICKETS_UI_FLAG, { isCreatingMessage: false });
    }
  },

  clearTicket: async function clearTicket({ commit }) {
    commit(types.CLEAR_SUPPORT_TICKET);
  },
};

export const mutations = {
  [types.SET_SUPPORT_TICKETS_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_SUPPORT_TICKETS]: MutationHelpers.set,
  [types.SET_SUPPORT_TICKET](_state, data) {
    _state.currentTicket = data;
  },
  [types.SET_SUPPORT_TICKETS_META](_state, meta) {
    _state.meta = { ..._state.meta, ...meta };
  },
  [types.ADD_SUPPORT_TICKET]: MutationHelpers.create,
  [types.UPDATE_SUPPORT_TICKET_STATUS](_state, { id, status }) {
    const index = _state.records.findIndex(record => record.id === Number(id));
    if (index !== -1) {
      _state.records.splice(index, 1, { ..._state.records[index], status });
    }
    if (_state.currentTicket && _state.currentTicket.id === Number(id)) {
      _state.currentTicket = { ..._state.currentTicket, status };
    }
  },
  [types.SET_SUPPORT_TICKET_MESSAGES](_state, messages) {
    _state.messages = [...messages];
  },
  [types.ADD_SUPPORT_TICKET_MESSAGE](_state, message) {
    _state.messages = [..._state.messages, message];
  },
  [types.CLEAR_SUPPORT_TICKET](_state) {
    _state.currentTicket = null;
    _state.messages = [];
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
